import React, { useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
  Plugin,
} from "chart.js";

// Registro único (barras por hora + linha de meta). Sem dependências novas.
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Tooltip,
  Legend,
  Filler,
);

export interface HourlyChartColors {
  bars: string[];
  meta: string;
  now: string;
  text: string;
  grid: string;
  /** Cor da faixa de fundo das horas fora de turno. */
  offShiftBand: string;
}

export interface HourlyChartProps {
  labels: string[];
  produced: number[];
  /** Meta horária constante repetida por hora (linha). */
  target: number;
  /** Alinhado a labels: true = hora fora do turno (faixa cinza, sem linha de meta). */
  offShift: boolean[];
  colors: HourlyChartColors;
  metaLabel: string;
  producedLabel: string;
  /** Índice (posição na série) da hora corrente para a linha "agora"; -1 se não for hoje. */
  nowIndex: number;
  nowLabel: string;
  locale: string;
  height?: number;
}

const HourlyChart: React.FC<HourlyChartProps> = ({
  labels,
  produced,
  target,
  offShift,
  colors,
  metaLabel,
  producedLabel,
  nowIndex,
  nowLabel,
  locale,
  height = 300,
}) => {
  // react-chartjs-2 v5 injeta `plugins` só na criação do gráfico (dist/index.js: em update()
  // apenas data/labels/options são reatribuídos, nunca config.plugins). Plugins inline por isso
  // congelam o closure do 1º render — era o motivo da faixa cinza fora-de-turno não sumir ao
  // trocar o turno (só "sumia" quando navegar de dia recriava o gráfico). Lendo os valores
  // dinâmicos via refs, os plugins (criados uma única vez) refletem os props atuais a cada draw.
  const offShiftRef = useRef(offShift);
  const labelsLenRef = useRef(labels.length);
  const bandColorRef = useRef(colors.offShiftBand);
  const nowIndexRef = useRef(nowIndex);
  const nowLabelRef = useRef(nowLabel);
  const nowColorRef = useRef(colors.now);
  offShiftRef.current = offShift;
  labelsLenRef.current = labels.length;
  bandColorRef.current = colors.offShiftBand;
  nowIndexRef.current = nowIndex;
  nowLabelRef.current = nowLabel;
  nowColorRef.current = colors.now;

  const fmt = (v: number, dec = 0): string =>
    Number(v).toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec });

  const data = useMemo<ChartData>(
    () => ({
      labels,
      datasets: [
        {
          type: "bar" as const,
          label: producedLabel,
          data: produced,
          backgroundColor: colors.bars,
          borderRadius: 3,
          maxBarThickness: 26,
          categoryPercentage: 0.9,
          barPercentage: 0.9,
          order: 2,
        },
        {
          type: "line" as const,
          label: metaLabel,
          // Meta só vale dentro do turno → sem ponto (null) nas horas fora do turno (linha quebra).
          data: labels.map((_, i) => (offShift[i] ? null : target)),
          borderColor: colors.meta,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHitRadius: 0,
          fill: false,
          tension: 0,
          spanGaps: false,
          order: 1,
        },
      ],
    }),
    [labels, produced, target, offShift, colors, producedLabel, metaLabel],
  );

  const options = useMemo<ChartOptions>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item: any) => {
              if (item.dataset.type === "line") return `${metaLabel}: ${fmt(Number(item.raw), 1)}`;
              return `${producedLabel}: ${fmt(Number(item.raw), 0)}`;
            },
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: colors.text, font: { size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
        y: { beginAtZero: true, grid: { color: colors.grid }, ticks: { color: colors.text, font: { size: 11 }, precision: 0 } },
      },
    }),
    [colors, metaLabel, producedLabel, locale],
  );

  // Faixa cinza de fundo nas horas fora do turno (08:00–17:00). Desenhada ANTES das barras.
  const offShiftPlugin = useMemo<Plugin>(
    () => ({
      id: "hourlyOffShift",
      beforeDatasetsDraw: (chart) => {
        const xScale = chart.scales?.x;
        const area = chart.chartArea;
        if (!xScale || !area) return;
        const off = offShiftRef.current;
        const n = labelsLenRef.current;
        if (n === 0) return;
        const step = n > 1 ? xScale.getPixelForValue(1) - xScale.getPixelForValue(0) : area.width;
        const half = Math.abs(step) / 2;
        const { ctx } = chart;
        ctx.save();
        ctx.fillStyle = bandColorRef.current;
        for (let i = 0; i < n; i++) {
          if (!off[i]) continue;
          const cx = xScale.getPixelForValue(i);
          if (cx == null || !Number.isFinite(cx)) continue;
          const left = Math.max(area.left, cx - half);
          const right = Math.min(area.right, cx + half);
          ctx.fillRect(left, area.top, right - left, area.bottom - area.top);
        }
        ctx.restore();
      },
    }),
    [],
  );

  // Linha vertical "agora" na hora corrente (só quando é hoje).
  const nowPlugin = useMemo<Plugin>(
    () => ({
      id: "hourlyNow",
      afterDatasetsDraw: (chart) => {
        const idx = nowIndexRef.current;
        if (idx < 0) return;
        const x = chart.scales?.x?.getPixelForValue(idx);
        const area = chart.chartArea;
        if (x == null || !Number.isFinite(x) || !area) return;
        const { ctx } = chart;
        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([3, 3]);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = nowColorRef.current;
        ctx.moveTo(x, area.top);
        ctx.lineTo(x, area.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = nowColorRef.current;
        ctx.font = "600 10px Montserrat, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(nowLabelRef.current, x, area.top - 3);
        ctx.restore();
      },
    }),
    [],
  );

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <Chart type="bar" data={data} options={options} plugins={[offShiftPlugin, nowPlugin]} />
    </div>
  );
};

export default HourlyChart;
